// src/utils/chunkRetry.js
/**
 * Utility functions to handle chunk loading failures in React applications
 * This helps resolve the "ChunkLoadError" issue that can occur during navigation
 */

/**
 * Checks if an error is a chunk loading error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a chunk loading error
 */
export const isChunkLoadError = (error) => {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('ChunkLoadError') ||
    error?.message?.includes('Loading CSS chunk') ||
    error?.message?.includes('Loading JS chunk') ||
    error?.stack?.includes('ChunkLoadError')
  );
};

/**
 * Reloads the page if it's a chunk loading error
 * This is a simple but effective fix for chunk loading issues
 * @param {Error} error - The error that occurred
 */
export const handleChunkError = (error) => {
  if (isChunkLoadError(error)) {
    console.warn('Chunk loading failed, reloading page:', error.message);
    
    // Add a small delay to prevent infinite reload loops
    setTimeout(() => {
      window.location.reload();
    }, 100);
    
    return true; // Indicates we handled the error
  }
  return false; // Indicates we didn't handle this error
};

/**
 * Creates a retry wrapper for lazy imports
 * @param {Function} importFn - The dynamic import function
 * @param {number} maxRetries - Maximum number of retries (default 3)
 * @returns {Function} - Wrapped import function with retry logic
 */
export const createRetryableImport = (importFn, maxRetries = 3) => {
  return async (...args) => {
    let lastError;
    
    for (let i = 0; i <= maxRetries; i++) {
      try {
        return await importFn(...args);
      } catch (error) {
        lastError = error;
        
        if (isChunkLoadError(error) && i < maxRetries) {
          console.warn(`Chunk loading failed (attempt ${i + 1}/${maxRetries + 1}), retrying...`, error.message);
          
          // Add exponential backoff
          const delay = Math.min(1000 * Math.pow(2, i), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          continue;
        }
        
        // If it's a chunk error and we've exhausted retries, reload the page
        if (isChunkLoadError(error)) {
          console.error('Failed to load chunk after retries, reloading page');
          window.location.reload();
          return;
        }
        
        throw error;
      }
    }
    
    throw lastError;
  };
};

/**
 * Enhanced lazy loading with chunk error handling
 * @param {Function} importFn - The dynamic import function
 * @param {number} maxRetries - Maximum number of retries
 * @returns {React.LazyExoticComponent} - Lazy component with error handling
 */
export const lazyWithRetry = (importFn, maxRetries = 3) => {
  const { lazy } = require('react');
  return lazy(createRetryableImport(importFn, maxRetries));
};

/**
 * Global error handler for unhandled chunk loading errors
 * Call this once in your app initialization
 */
export const setupGlobalChunkErrorHandler = () => {
  // Handle unhandled promise rejections (chunk loading errors)
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      console.warn('Unhandled chunk loading error, preventing crash:', event.reason);
      event.preventDefault(); // Prevent the error from being logged as unhandled
      handleChunkError(event.reason);
    }
  });
  
  // Handle general JavaScript errors
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
      console.warn('Global chunk loading error detected:', event.error);
      handleChunkError(event.error);
    }
  });
};
